// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { apiCall } from "../api/client";
import { useQueryClient } from "@tanstack/react-query";
import { signOutNativeGoogle } from "@/lib/nativeGoogleAuth";
import { unregisterNativePushForLogout } from "@/lib/nativePushNotifications";
import { getNativeAuthReturnScheme } from "@/lib/nativeAppLinks";

const AuthContext = createContext(null);

// 🔧 Normalisera user-objekt så vi alltid har role/club_slug
function normalizeUser(u) {
  if (!u || typeof u !== "object") return null;
  // bygga upp ett visningsnamn som UI kan lita på:
  const displayName =
    u.display_name ??
    u.username ??
    (typeof u.email === "string" && u.email.includes("@") ? u.email.split("@")[0] : null);

  return {
    ...u,
    display_name: displayName ?? null,
    // se till att UI alltid kan läsa user.username (behåll fältet men spegla display_name)
    username: displayName ?? null,
    role: u.role || "user",         // defaulta till 'user' om backend inte skickar med
    club_slug: u.club_slug ?? null, // null om saknas
    email_verified: u.email_verified ?? true,
  };
  // return {
  //   ...u,
  //   role: u.role || "user",            // defaulta till 'user' om backend inte skickar med
  //   club_slug: u.club_slug ?? null,    // null om saknas
  // };
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  // bumpas vid login/logout (och när 2FA blir klar) för att invalidatera user-scoped queries
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    const u = localStorage.getItem("speedway_user");
    if (u) {
      try {
        const parsed = JSON.parse(u);
        const norm = normalizeUser(parsed);
        if (norm) setUser(norm);
      } catch { }
    }
    setReady(true);
  }, []);

  // Håll alla flikar i synk: om token/user tas bort i EN flik → logga ut i denna också  TESTAR
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "speedway_token" || e.key === "speedway_user") {
        const token = localStorage.getItem("speedway_token");
        const u = localStorage.getItem("speedway_user");
        if (!token || !u) {
          setUser(null);
          // Om man står på skyddad sida så tar RequireAuth över och skickar till /auth
        } else {
          try {
            const norm = normalizeUser(JSON.parse(u));
            setUser(norm);
          } catch { }
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // 🆕 Lyssna på global "auth:logout" → nolla user och töm cache
  useEffect(() => {
    const onForcedLogout = () => {
      setUser(null);
      try { queryClient.clear(); } catch { }
    };
    window.addEventListener("auth:logout", onForcedLogout);
    return () => window.removeEventListener("auth:logout", onForcedLogout);
  }, [queryClient]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEG 1: login → kan returnera token ELLER 2FA-biljett
  const login = async (email, password) => {
    const res = await apiCall("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    if (res?.two_factor_required) {
      // backend bör redan skicka user här – normalisera ifall role/club_slug saknas
      const normUser = normalizeUser(res.user);
      return {
        twoFactorRequired: true,
        ticket: res.ticket,
        deviceLabel: res.device_label || "Ny enhet",
        user: normUser,
      };
    }

    if (res?.token && res?.user) {
      const normUser = normalizeUser(res.user);
      localStorage.setItem("speedway_token", res.token);
      localStorage.setItem("speedway_user", JSON.stringify(normUser));
      setUser(normUser);
      setAuthVersion(v => v + 1);
      return { ok: true, user: normUser };
    }

    throw new Error("Oväntat svar från servern vid inloggning.");
  };

  const oauthLogin = async (provider, idToken) => {
    const res = await apiCall(`/api/auth/oauth/${provider}`, {
      method: "POST",
      body: JSON.stringify({ id_token: idToken }),
    });

    if (res?.two_factor_required) {
      const normUser = normalizeUser(res.user);
      return {
        twoFactorRequired: true,
        ticket: res.ticket,
        deviceLabel: res.device_label || "Ny enhet",
        user: normUser,
      };
    }

    if (res?.token && res?.user) {
      const normUser = normalizeUser(res.user);
      localStorage.setItem("speedway_token", res.token);
      localStorage.setItem("speedway_user", JSON.stringify(normUser));
      setUser(normUser);
      setAuthVersion(v => v + 1);
      return { ok: true, user: normUser };
    }

    throw new Error("Oväntat svar från servern vid OAuth-inloggning.");
  };

  // STEG 2: verifiera 2FA-kod – då får vi token + user
  const verify2FA = async (ticket, code) => {
    const res = await apiCall("/api/auth/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    });
    if (res?.token && res?.user) {
      const normUser = normalizeUser(res.user);
      localStorage.setItem("speedway_token", res.token);
      localStorage.setItem("speedway_user", JSON.stringify(normUser));
      setUser(normUser);
      setAuthVersion(v => v + 1);
      return { ok: true, user: normUser };
    }
    throw new Error("Oväntat svar från servern vid 2FA-verifiering.");
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Registrering (ingen 2FA-hantering)
  // OBS: backend MÅSTE hårdkoda role="user" här; UI skickar aldrig role.
  const register = async (email, password) => {
    const res = await apiCall("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        native_return_scheme: getNativeAuthReturnScheme(),
      }),
    });
    return res;
  };

  const resendVerificationEmail = async (email) => {
    const res = await apiCall("/api/auth/verify-email/resend", {
      method: "POST",
      body: JSON.stringify({
        email,
        native_return_scheme: getNativeAuthReturnScheme(),
      }),
    });
    return res;
  };



  // ─────────────────────────────────────────────────────────────────────────────
  // Logga ut: rensa token + user, töm cache, bumpa authVersion, skicka global event
  const logout = () => {
    unregisterNativePushForLogout();
    void signOutNativeGoogle().catch(() => { });
    try {
      localStorage.removeItem("speedway_token");
      localStorage.removeItem("speedway_user");
    } catch { }
    // 🆕 Visa trevlig toast på /auth efter redirect
    sessionStorage.setItem("auth_notice", "Du har loggats ut.");
    setUser(null);
    try { queryClient.clear(); } catch { }
    setAuthVersion(v => v + 1);
    // 🆕 Sänd samma event som vid 401 så allt beter sig lika
    try {
      window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason: "manual" } }));
    } catch { }
  };

  return (
    <AuthContext.Provider value={{ user, ready, authVersion, login, oauthLogin, verify2FA, register, resendVerificationEmail, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

